import type { BoardChat } from '../../interfaces/BoardChat';
import type { BoardDetails } from '../../interfaces/BoardDetails';
import type { Idea } from '../../interfaces/Idea';
import type { IdeaComment } from '../../interfaces/IdeaComment';
import type { IdeaVote } from '../../interfaces/IdeaVote';
import { dotvotingApi } from '../../services/dotvotingApi';
import useUserStore from '../userStore';
import {
  applyParticipantDeletionToState,
  getParticipantNameKey,
  isCurrentUserDeletedParticipant,
  isSameChatMessage,
  mapBoardParticipants,
  mapSocketIdea,
  mapSocketParticipant,
  prependOrReplaceIdeas,
  sanitizeBoardDetails,
  syncViewCard,
} from './helpers';
import type { BoardState, BoardStoreGet, BoardStoreSet } from './types';

export const createSocketActions = (
  set: BoardStoreSet,
  get: BoardStoreGet
): Pick<BoardState, 'applySocketEvent'> => ({
  applySocketEvent: async (event) => {
    const { boardId } = get();
    if (!boardId || event.topic !== boardId) {
      return;
    }

    if (event.action === 'topicIdeaAdded') {
      const socketIdea = mapSocketIdea(event.idea, boardId);
      if (socketIdea) {
        set((state) => {
          const ideas = prependOrReplaceIdeas(state.ideas, [socketIdea]);
          return {
            ideas,
            viewCard: syncViewCard(ideas, state.viewCard),
          };
        });
        return;
      }

      if (typeof event.ideaId !== 'string' || !event.ideaId.trim()) {
        return;
      }

      try {
        const idea = await dotvotingApi.getIdea(boardId, event.ideaId);
        set((state) => {
          const ideas = prependOrReplaceIdeas(state.ideas, [idea]);
          return {
            ideas,
            viewCard: syncViewCard(ideas, state.viewCard),
          };
        });
      } catch {
        // Ignore event if the idea can no longer be retrieved.
      }
      return;
    }

    if (event.action === 'topicIdeaAttachmentInserted') {
      const ideaIds = Array.isArray(event.ideaIds)
        ? event.ideaIds.filter((ideaId): ideaId is string => typeof ideaId === 'string')
        : [];
      if (ideaIds.length === 0) {
        return;
      }

      const uniqueIdeaIds = Array.from(new Set(ideaIds));
      const fetchedIdeas = (
        await Promise.all(
          uniqueIdeaIds.map(async (ideaId) => {
            try {
              return await dotvotingApi.getIdea(boardId, ideaId);
            } catch {
              return null;
            }
          })
        )
      ).filter((idea): idea is Idea => idea !== null);

      if (fetchedIdeas.length === 0) {
        return;
      }

      set((state) => {
        const ideas = prependOrReplaceIdeas(state.ideas, fetchedIdeas);
        return {
          ideas,
          viewCard: syncViewCard(ideas, state.viewCard),
        };
      });
      return;
    }

    if (event.action === 'topicIdeaUpdated') {
      const ideaPayload = event.idea as
        | {
            id?: unknown;
            title?: unknown;
            content?: unknown;
            mimeType?: unknown;
          }
        | undefined;
      const payloadIdeaId = typeof ideaPayload?.id === 'string' ? ideaPayload.id : null;
      const eventIdeaId = typeof event.ideaId === 'string' ? event.ideaId : null;
      const ideaId = payloadIdeaId ?? eventIdeaId;
      if (!ideaId?.trim()) {
        return;
      }
      const nextContent =
        ideaPayload?.content === null
          ? null
          : typeof ideaPayload?.content === 'string'
          ? ideaPayload.content
          : undefined;
      const nextTitle =
        ideaPayload?.title === null
          ? null
          : typeof ideaPayload?.title === 'string'
          ? ideaPayload.title
          : undefined;
      const nextMimeType =
        ideaPayload?.mimeType === null
          ? null
          : typeof ideaPayload?.mimeType === 'string'
          ? ideaPayload.mimeType
          : undefined;

      if (nextContent !== undefined || nextTitle !== undefined || nextMimeType !== undefined) {
        set((state) => {
          const ideas = state.ideas.map((idea) => {
            if (idea.id !== ideaId) {
              return idea;
            }

            return {
              ...idea,
              ...(nextContent !== null && nextContent !== undefined
                ? { content: nextContent }
                : {}),
              ...(nextTitle !== null && nextTitle !== undefined ? { title: nextTitle } : {}),
              ...(nextMimeType !== null && nextMimeType !== undefined
                ? { mimeType: nextMimeType }
                : {}),
            };
          });
          return {
            ideas,
            viewCard: syncViewCard(ideas, state.viewCard),
          };
        });
        return;
      }

      try {
        const idea = await dotvotingApi.getIdea(boardId, ideaId);
        set((state) => {
          const ideas = prependOrReplaceIdeas(state.ideas, [idea]);
          return {
            ideas,
            viewCard: syncViewCard(ideas, state.viewCard),
          };
        });
      } catch {
        // Ignore event if the idea can no longer be retrieved.
      }
      return;
    }

    if (event.action === 'topicIdeaPinned') {
      set((state) => {
        const ideas = state.ideas.map((idea) =>
          idea.id === event.ideaId ? { ...idea, pinned: true } : idea
        );
        return {
          ideas,
          viewCard: syncViewCard(ideas, state.viewCard),
        };
      });
      return;
    }

    if (event.action === 'topicIdeaUnpinned') {
      set((state) => {
        const ideas = state.ideas.map((idea) =>
          idea.id === event.ideaId ? { ...idea, pinned: false } : idea
        );
        return {
          ideas,
          viewCard: syncViewCard(ideas, state.viewCard),
        };
      });
      return;
    }

    if (event.action === 'sortTopicCards') {
      const cardIds = Array.isArray(event.cardIds)
        ? event.cardIds.filter((cardId): cardId is string => typeof cardId === 'string')
        : [];

      if (cardIds.length === 0) {
        return;
      }

      const orderByCardId = new Map(cardIds.map((cardId, index) => [cardId, index + 1]));

      set((state) => {
        const ideas = state.ideas.map((idea) => {
          const nextSortNo = orderByCardId.get(idea.id);

          return {
            ...idea,
            sortNo: nextSortNo ?? cardIds.length + 1,
          };
        });

        return {
          ideas,
          viewCard: syncViewCard(ideas, state.viewCard),
        };
      });
      return;
    }

    if (event.action === 'topicIdeaColumnChanged') {
      const ideaId = typeof event.ideaId === 'string' ? event.ideaId : '';
      const columnId = typeof event.columnId === 'string' ? event.columnId : '';

      if (!ideaId || !columnId) {
        return;
      }

      set((state) => {
        const ideas = state.ideas.map((idea) =>
          idea.id === ideaId ? { ...idea, columnId } : idea
        );

        return {
          ideas,
          viewCard: syncViewCard(ideas, state.viewCard),
        };
      });
      return;
    }

    if (event.action === 'topicIdeaDeleted') {
      const ideaPayload = event.idea as
        | {
            id?: unknown;
          }
        | undefined;

      if (!ideaPayload?.id) {
        return;
      }

      set((state) => {
        const ideas = state.ideas.filter((idea) => idea.id !== ideaPayload.id);
        return {
          ideas,
          viewCard: syncViewCard(ideas, state.viewCard),
        };
      });
      return;
    }

    if (event.action === 'topicIdeaApproved') {
      if (typeof event.ideaId !== 'string' || !event.ideaId.trim()) {
        return;
      }

      set((state) => {
        const ideas = state.ideas.map((idea) =>
          idea.id === event.ideaId ? { ...idea, approved: true } : idea
        );

        return {
          ideas,
          viewCard: syncViewCard(ideas, state.viewCard),
        };
      });
      return;
    }

    if (event.action === 'topicCandidateVoted') {
      const votePayload = event.vote as
        | {
            id?: unknown;
            ideaId?: unknown;
            ownerId?: unknown;
          }
        | undefined;

      if (
        typeof votePayload?.ideaId !== 'string' ||
        typeof votePayload?.id !== 'string' ||
        typeof votePayload?.ownerId !== 'string'
      ) {
        return;
      }

      const vote: IdeaVote = {
        id: votePayload.id,
        ownerId: votePayload.ownerId,
      };

      set((state) => {
        const ideas = state.ideas.map((idea) => {
          if (
            idea.id !== votePayload.ideaId ||
            idea.votes.some((existingVote) => existingVote.id === vote.id)
          ) {
            return idea;
          }
          return {
            ...idea,
            votes: [...idea.votes, vote],
          };
        });
        return {
          ideas,
          viewCard: syncViewCard(ideas, state.viewCard),
        };
      });
      return;
    }

    if (event.action === 'topicCandidateVoteRemoved') {
      const votePayload = event.vote as
        | {
            id?: unknown;
            ideaId?: unknown;
          }
        | undefined;

      if (typeof votePayload?.ideaId !== 'string' || typeof votePayload?.id !== 'string') {
        return;
      }

      set((state) => {
        const ideas = state.ideas.map((idea) =>
          idea.id === votePayload.ideaId
            ? { ...idea, votes: idea.votes.filter((vote) => vote.id !== votePayload.id) }
            : idea
        );
        return {
          ideas,
          viewCard: syncViewCard(ideas, state.viewCard),
        };
      });
      return;
    }

    if (event.action === 'ideaCommentAdded') {
      const commentPayload = event.comment as
        | {
            id?: unknown;
            ideaId?: unknown;
            ownerId?: unknown;
            content?: unknown;
            added?: unknown;
          }
        | undefined;

      if (
        typeof commentPayload?.ideaId !== 'string' ||
        typeof commentPayload?.id !== 'string' ||
        typeof commentPayload?.ownerId !== 'string' ||
        typeof commentPayload?.content !== 'string' ||
        typeof commentPayload?.added !== 'string'
      ) {
        return;
      }

      const comment: IdeaComment = {
        id: commentPayload.id,
        ownerId: commentPayload.ownerId,
        content: commentPayload.content,
        added: commentPayload.added,
      };

      set((state) => {
        const ideas = state.ideas.map((idea) => {
          if (
            idea.id !== commentPayload.ideaId ||
            idea.comments.some((existingComment) => existingComment.id === comment.id)
          ) {
            return idea;
          }

          return {
            ...idea,
            comments: [...idea.comments, comment],
          };
        });

        return {
          ideas,
          viewCard: syncViewCard(ideas, state.viewCard),
        };
      });
      return;
    }

    if (event.action === 'ideaCommentDeleted') {
      const commentPayload = event.comment as
        | {
            id?: unknown;
            ideaId?: unknown;
          }
        | undefined;

      if (typeof commentPayload?.ideaId !== 'string' || typeof commentPayload?.id !== 'string') {
        return;
      }

      set((state) => {
        const ideas = state.ideas.map((idea) =>
          idea.id === commentPayload.ideaId
            ? {
                ...idea,
                comments: idea.comments.filter((comment) => comment.id !== commentPayload.id),
              }
            : idea
        );

        return {
          ideas,
          viewCard: syncViewCard(ideas, state.viewCard),
        };
      });
      return;
    }

    if (event.action === 'topicUpdated') {
      const updates =
        event.updates && typeof event.updates === 'object'
          ? (event.updates as Partial<BoardDetails>)
          : null;

      if (!updates) {
        return;
      }

      set((state) => {
        if (!state.boardDetails) {
          return state;
        }

        const boardDetails = sanitizeBoardDetails({ ...state.boardDetails, ...updates });
        return {
          boardDetails,
          participants: mapBoardParticipants(boardDetails),
        };
      });
      return;
    }

    if (event.action === 'topicIdeasLocked' || event.action === 'topicIdeasUnlocked') {
      const locked = event.action === 'topicIdeasLocked';

      set((state) => {
        if (!state.boardDetails) {
          return state;
        }

        return {
          boardDetails: sanitizeBoardDetails({
            ...state.boardDetails,
            locked,
          }),
        };
      });
      return;
    }

    if (event.action === 'topicVotingDisabled' || event.action === 'topicVotingEnabled') {
      const votingEnabled = event.action === 'topicVotingEnabled';

      set((state) => {
        if (!state.boardDetails) {
          return state;
        }

        return {
          boardDetails: sanitizeBoardDetails({
            ...state.boardDetails,
            votingEnabled,
          }),
        };
      });
      return;
    }

    if (
      event.action === 'topicPrivateVotingEnabled' ||
      event.action === 'topicPrivateVotingDisabled'
    ) {
      const privateVotingEnabled = event.action === 'topicPrivateVotingEnabled';

      set((state) => {
        if (!state.boardDetails) {
          return state;
        }

        return {
          boardDetails: sanitizeBoardDetails({
            ...state.boardDetails,
            privateVotingEnabled,
          }),
        };
      });
      return;
    }

    if (event.action === 'topicCommentingDisabled' || event.action === 'topicCommentingEnabled') {
      const commentsEnabled = event.action === 'topicCommentingEnabled';

      set((state) => {
        if (!state.boardDetails) {
          return state;
        }

        return {
          boardDetails: sanitizeBoardDetails({
            ...state.boardDetails,
            commentsEnabled,
          }),
        };
      });
      return;
    }

    if (event.action === 'topicGiphyEnabled') {
      const giphyEnabled = typeof event.enabled === 'boolean' ? event.enabled : null;
      if (giphyEnabled === null) {
        return;
      }

      set((state) => {
        if (!state.boardDetails) {
          return state;
        }

        return {
          boardDetails: sanitizeBoardDetails({
            ...state.boardDetails,
            giphyEnabled,
          }),
        };
      });
      return;
    }

    if (event.action === 'topicChatEnabled' || event.action === 'topicChatDisabled') {
      const chatEnabled = event.action === 'topicChatEnabled';

      set((state) => {
        if (!state.boardDetails) {
          return state;
        }

        return {
          boardDetails: sanitizeBoardDetails({
            ...state.boardDetails,
            chatEnabled,
          }),
        };
      });
      return;
    }

    if (
      event.action === 'topicParticipantsEnabled' ||
      event.action === 'topicParticipantsDisabled'
    ) {
      const participantsEnabled = event.action === 'topicParticipantsEnabled';

      set((state) => {
        if (!state.boardDetails) {
          return state;
        }

        return {
          boardDetails: sanitizeBoardDetails({
            ...state.boardDetails,
            participantsEnabled,
          }),
        };
      });
      return;
    }

    if (event.action === 'votesCleared') {
      set((state) => {
        const ideas = state.ideas.map((idea) => ({
          ...idea,
          votes: [],
        }));

        return {
          ideas,
          viewCard: syncViewCard(ideas, state.viewCard),
        };
      });
      return;
    }

    if (event.action === 'topicParticipantAdded') {
      const participant = mapSocketParticipant(event.participant);
      if (!participant) {
        return;
      }

      set((state) => {
        const participantExists = state.participants.some(
          (existingParticipant) => existingParticipant.id === participant.id
        );

        if (participantExists) {
          return state;
        }

        return {
          participants: [...state.participants, participant].sort((left, right) =>
            left.name.localeCompare(right.name, undefined, { sensitivity: 'base' })
          ),
        };
      });
      return;
    }

    if (event.action === 'topicParticipantDeleted') {
      const participant = mapSocketParticipant(event.participant);
      const participantId = participant?.id ?? null;
      const participantNameKey = participant ? getParticipantNameKey(participant.name) : null;
      if (!participantId && !participantNameKey) {
        return;
      }

      set((state) => {
        const removedParticipantIds = new Set<string>();
        if (participantId) {
          removedParticipantIds.add(participantId);
        }

        const removedParticipantNameKeys = new Set<string>();
        if (participantNameKey) {
          removedParticipantNameKeys.add(participantNameKey);
        }

        return applyParticipantDeletionToState(
          state,
          removedParticipantIds,
          removedParticipantNameKeys
        );
      });

      if (
        isCurrentUserDeletedParticipant(
          participantId ? new Set([participantId]) : new Set<string>(),
          participantNameKey ? new Set([participantNameKey]) : new Set<string>()
        )
      ) {
        useUserStore.getState().setCurrentUser(null);
      }
      return;
    }

    if (event.action === 'topicChatAdded') {
      const rawChat = event.chat as Partial<BoardChat> | undefined;
      const chat =
        rawChat &&
        typeof rawChat.ownerName === 'string' &&
        typeof rawChat.content === 'string' &&
        typeof rawChat.created === 'string'
          ? {
              ownerName: rawChat.ownerName,
              content: rawChat.content,
              created: rawChat.created,
            }
          : null;

      if (!chat) {
        return;
      }

      set((state) => {
        if (state.chat.some((existingChat) => isSameChatMessage(existingChat, chat))) {
          return state;
        }

        return {
          chat: [...state.chat, chat],
        };
      });
      return;
    }

    if (event.action === 'topicVideoProcessed') {
      const idea = event.idea as Idea | undefined;
      if (!idea || typeof idea.id !== 'string') {
        return;
      }

      set((state) => {
        const ideas = prependOrReplaceIdeas(state.ideas, [idea]);

        return {
          ideas,
          viewCard: syncViewCard(ideas, state.viewCard),
        };
      });
    }
  },
});
